import { AllowedWebsiteSchemaUnmanaged, IpWebsiteType, IpxRbsWorkflowClass, OrganizationAllowedWebsiteSource, OrganizationRpaBuilderWebsiteRequiredTriggerConfigSchema, SessionPersistenceType, ThirdPartySystemId, UrlPatternStringSchema, UniqueUrlPatternStringListSchema, UrlPatternsRuleAction, } from '@pillar-portal/api-types';
export const RPA_BUILDER_ALWAYS_ACTIVE_IPS_TRIGGER_URL_PATTERN = 'https://*/*';
function buildUrlPatternsText(patterns) {
    return patterns.join('\n');
}
function parseUrlPatternsText(value) {
    return value
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
}
function buildRuleEditorState(input) {
    return {
        action: input.action,
        urlPatternsText: buildUrlPatternsText(input.urlPatterns),
    };
}
export function buildRpaBuilderWebsitePolicyEditorState(website) {
    return {
        name: website?.name ?? '',
        url: website?.url ?? '',
        allowedUrlPatternsText: buildUrlPatternsText(website?.allowedUrlPatterns ?? []),
        sessionPersistence: website?.sessionPersistence ?? SessionPersistenceType.PERSISTENT,
        promoteSessionCookies: website?.promoteSessionCookies ?? false,
        urlPatternsRules: website?.urlPatternsRules.map(buildRuleEditorState) ?? [],
        fetchDefaultAction: website?.fetchDefaultAction ?? UrlPatternsRuleAction.ALLOW,
        iframeUrlPatterns: website?.iframeUrlPatterns.map(buildRuleEditorState) ?? [],
        iframeDefaultAction: website?.iframeDefaultAction ?? UrlPatternsRuleAction.ALLOW,
    };
}
export function createRpaBuilderWebsiteEditorModel(input) {
    const currentWebsite = input.currentWebsite;
    if (input.mode === 'edit' && currentWebsite === undefined) {
        throw new Error('Current organization website is required for edit');
    }
    const rbsWebsite = currentWebsite?.rbs_website ?? null;
    return {
        mode: input.mode,
        organizationAllowedWebsiteUuid: currentWebsite?.organization_allowed_website_uuid ?? null,
        selectedTemplate: null,
        expectedWebsiteConfig: currentWebsite?.website_config ?? null,
        rbsWebsite,
        websitePolicy: buildRpaBuilderWebsitePolicyEditorState(currentWebsite?.website_config),
        ipsTriggerUrlPatterns: rbsWebsite === null
            ? [RPA_BUILDER_ALWAYS_ACTIVE_IPS_TRIGGER_URL_PATTERN]
            : rbsWebsite.rbs_config.ips_trigger_url_patterns.length === 0
                ? ['']
                : [...rbsWebsite.rbs_config.ips_trigger_url_patterns],
        reconciliationPaymentTypeCode: rbsWebsite?.rbs_config.workflow_input_defaults?.payment_type_code ?? '',
        canEditRbsSettings: input.canEditRbsSettings ?? currentWebsite?.capabilities.can_edit ?? true,
        canEditWebsitePolicy: input.canEditWebsitePolicy ??
            (currentWebsite === undefined ||
                (currentWebsite.capabilities.can_edit &&
                    currentWebsite.source !== OrganizationAllowedWebsiteSource.TEMPLATE_REFERENCE)),
    };
}
export function selectRpaBuilderWebsiteTemplate(model, template) {
    if (model.mode !== 'add') {
        throw new Error('Templates can only be selected while adding a website');
    }
    if (template.rbs_website === null) {
        throw new Error('Selected template is not configured for RPA Builder');
    }
    return {
        ...model,
        selectedTemplate: template,
        rbsWebsite: template.rbs_website,
        websitePolicy: buildRpaBuilderWebsitePolicyEditorState(template.website_config),
        ipsTriggerUrlPatterns: template.rbs_website.rbs_config.ips_trigger_url_patterns.length === 0
            ? ['']
            : [...template.rbs_website.rbs_config.ips_trigger_url_patterns],
        reconciliationPaymentTypeCode: template.rbs_website.rbs_config.workflow_input_defaults?.payment_type_code ?? '',
    };
}
export function clearRpaBuilderWebsiteTemplate(model) {
    if (model.mode !== 'add') {
        return model;
    }
    return {
        ...model,
        selectedTemplate: null,
        rbsWebsite: null,
        websitePolicy: buildRpaBuilderWebsitePolicyEditorState(),
    };
}
export function getRpaBuilderIpsTriggerUrlMode(patterns) {
    const normalized = normalizePatterns(patterns);
    return normalized.length === 1 &&
        normalized[0] === RPA_BUILDER_ALWAYS_ACTIVE_IPS_TRIGGER_URL_PATTERN
        ? 'always-active'
        : 'trigger-urls';
}
function normalizePatterns(patterns) {
    return patterns.map((pattern) => pattern.trim()).filter(Boolean);
}
function getRequiredPatternValidationMessage(patterns, label, rejectDuplicates) {
    const normalized = normalizePatterns(patterns);
    if (normalized.length === 0) {
        return `At least one ${label} is required.`;
    }
    const seen = new Set();
    for (const pattern of normalized) {
        if (!UrlPatternStringSchema.safeParse(pattern).success) {
            return `URL pattern "${pattern}" is invalid. Use URLPattern syntax such as https://*.example.com/*.`;
        }
        if (rejectDuplicates && seen.has(pattern)) {
            return `URL pattern "${pattern}" is duplicated.`;
        }
        seen.add(pattern);
    }
    return null;
}
function getWebsitePolicyValidationMessage(policy) {
    const url = policy.url.trim();
    if (url.length === 0) {
        return 'URL is required. Enter a complete URL such as https://example.com.';
    }
    try {
        new URL(url);
    }
    catch {
        return `URL "${url}" is invalid. Enter a complete URL such as https://example.com.`;
    }
    const patternGroups = [
        parseUrlPatternsText(policy.allowedUrlPatternsText),
        ...policy.urlPatternsRules.map((rule) => parseUrlPatternsText(rule.urlPatternsText)),
        ...policy.iframeUrlPatterns.map((rule) => parseUrlPatternsText(rule.urlPatternsText)),
    ];
    for (const patterns of patternGroups) {
        const validationMessage = getRequiredPatternValidationMessage(patterns, 'URL pattern', false);
        if (validationMessage !== null) {
            return validationMessage;
        }
    }
    // Traceability: implements RBS-WEBSITE-POLICY-UNIQUE-001
    for (const patterns of [
        parseUrlPatternsText(policy.allowedUrlPatternsText),
        policy.urlPatternsRules.flatMap((rule) => parseUrlPatternsText(rule.urlPatternsText)),
        policy.iframeUrlPatterns.flatMap((rule) => parseUrlPatternsText(rule.urlPatternsText)),
    ]) {
        const result = UniqueUrlPatternStringListSchema.safeParse(patterns);
        if (!result.success)
            return 'Duplicate URL pattern. Remove the duplicate from this policy list before saving.';
    }
    return null;
}
export function getRpaBuilderWebsiteEditorValidationMessage(model) {
    if (model.selectedTemplate === null && model.canEditWebsitePolicy) {
        const policyMessage = getWebsitePolicyValidationMessage(model.websitePolicy);
        if (policyMessage !== null) {
            return policyMessage;
        }
    }
    return getRequiredPatternValidationMessage(model.ipsTriggerUrlPatterns, 'IPS trigger URL pattern', true);
}
function buildWebsiteConfig(policy) {
    return AllowedWebsiteSchemaUnmanaged.parse({
        type: IpWebsiteType.UNMANAGED,
        system_id: ThirdPartySystemId.GENERIC,
        name: policy.name.trim(),
        url: policy.url.trim(),
        allowedUrlPatterns: parseUrlPatternsText(policy.allowedUrlPatternsText),
        sessionPersistence: policy.sessionPersistence,
        promoteSessionCookies: policy.promoteSessionCookies,
        urlPatternsRules: policy.urlPatternsRules.map((rule) => ({
            action: rule.action,
            urlPatterns: parseUrlPatternsText(rule.urlPatternsText),
        })),
        fetchDefaultAction: policy.fetchDefaultAction,
        iframeUrlPatterns: policy.iframeUrlPatterns.map((rule) => ({
            action: rule.action,
            urlPatterns: parseUrlPatternsText(rule.urlPatternsText),
        })),
        iframeDefaultAction: policy.iframeDefaultAction,
    });
}
function buildRbsConfig(model) {
    const paymentTypeCode = model.reconciliationPaymentTypeCode.trim();
    return OrganizationRpaBuilderWebsiteRequiredTriggerConfigSchema.parse({
        ips_trigger_url_patterns: normalizePatterns(model.ipsTriggerUrlPatterns),
        ...(paymentTypeCode.length === 0
            ? {}
            : {
                workflow_input_defaults: {
                    payment_type_code: paymentTypeCode,
                },
            }),
    });
}
function buildNewRbsWebsite(model) {
    return {
        rbs_config: buildRbsConfig(model),
        web_resource_model: null,
        login_workflow: null,
        workflows: {
            [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: [],
            [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: [],
            [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: [],
            [IpxRbsWorkflowClass.RECONCILE]: [],
        },
    };
}
function assertValid(model) {
    const validationMessage = getRpaBuilderWebsiteEditorValidationMessage(model);
    if (validationMessage !== null) {
        throw new Error(validationMessage);
    }
}
export function buildCreateLocalRpaBuilderWebsiteRequest(model) {
    assertValid(model);
    return {
        website_config: buildWebsiteConfig(model.websitePolicy),
        rbs_website: buildNewRbsWebsite(model),
    };
}
export function buildUpdateRpaBuilderWebsiteRequest(model) {
    assertValid(model);
    let websitePolicyUpdate = {};
    if (model.canEditWebsitePolicy) {
        if (model.expectedWebsiteConfig === null) {
            throw new Error('Expected website policy is required for edit');
        }
        websitePolicyUpdate = {
            website_config: buildWebsiteConfig(model.websitePolicy),
            expected_website_config: model.expectedWebsiteConfig,
        };
    }
    return {
        ...websitePolicyUpdate,
        ...(model.canEditRbsSettings ? { rbs_config: buildRbsConfig(model) } : {}),
    };
}
//# sourceMappingURL=rpa-builder-website-editor-model.js.map